import { API_ENDPOINTS } from '../constants';
import { apiClient } from './apiClient';
import type { ListParams, Paginated } from './agentService';

export type McpMasterServer = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  transportType: string;
  endpoint?: string | null;
  authScheme?: string | null;
  status: string;
  isActive: boolean;
  metadata?: Record<string, any>;
};

export type McpToolDefinition = {
  id: string;
  toolName: string;
  title?: string | null;
  description?: string | null;
  status: string;
  riskLevel?: string | null;
  isEnabled: boolean;
  serverVersion?: {
    id: string;
    version: string;
    discoveredAt?: string | null;
    lastSyncedAt?: string | null;
  } | null;
};

export type McpServerListResult = Paginated<McpMasterServer>;
export type McpToolListResult = Paginated<McpToolDefinition>;

export type CreateMcpServerPayload = {
  code: string;
  name: string;
  description?: string;
  transportType?: string;
  endpoint?: string;
  authScheme?: string;
  status?: string;
  isActive?: boolean;
  metadata?: Record<string, any>;
};

export type UpdateMcpServerPayload = Partial<CreateMcpServerPayload>;

export type ValidateMcpServerResult = {
  ok: boolean;
  latencyMs?: number;
  toolCount?: number;
  instructions?: string | null;
};

export type DiscoverMcpToolsResult = {
  serverVersion?: {
    id: string;
    version: string;
    status: string;
    discoveredAt?: string | null;
    lastSyncedAt?: string | null;
  };
  tools: McpToolDefinition[];
  syncedCount: number;
};

export type StartManagedOAuthPayload = {
  environment?: string;
  transportConfig?: Record<string, any>;
  authConfig: {
    clientId?: string;
    clientSecret?: string;
    clientName?: string;
    redirectUri: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    scopes?: string[];
    extraAuthorizeParams?: Record<string, string>;
    hubspotWebhookSigningSecret?: string;
  };
};

export type ManagedMcpIntegration = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  transportType: string;
  authScheme?: string | null;
  status: string;
  metadata?: Record<string, any> | null;
  toolCount: number;
  platformOAuthReady?: boolean;
  connection?: {
    id: string;
    status: string;
    isEnabled: boolean;
    profileId: string;
    lastValidatedAt?: string | null;
    environment?: string | null;
    /** Existing stored credentials — used to pre-fill the edit form instead of showing it blank. */
    authConfig?: Record<string, any> | null;
    /** e.g. `{ url: string }` — the configured base URL, if any. */
    transportConfig?: Record<string, any> | null;
  } | null;
};

const buildListQuery = (params?: ListParams) => {
  const qs = new URLSearchParams();
  if (!params) return '';
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  if (params.isActive !== undefined) qs.set('isActive', String(params.isActive));
  if (params.includeDeleted) qs.set('includeDeleted', String(params.includeDeleted));
  return qs.toString();
};

export const mcpService = {
  async listMasterServers(params?: ListParams): Promise<Paginated<McpMasterServer>> {
    const query = buildListQuery(params);
    const path = API_ENDPOINTS.MCP.SERVERS.LIST;
    const url = query ? `${path}?${query}` : path;
    const result = await apiClient.get<Paginated<McpMasterServer>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async createMasterServer(payload: CreateMcpServerPayload): Promise<McpMasterServer> {
    const result = await apiClient.post<McpMasterServer>(API_ENDPOINTS.MCP.SERVERS.CREATE, payload, {
      authenticate: true,
    });
    return (result as any).data ?? result;
  },

  async updateMasterServer(serverId: string, payload: UpdateMcpServerPayload): Promise<McpMasterServer> {
    const result = await apiClient.patch<McpMasterServer>(
      API_ENDPOINTS.MCP.SERVERS.UPDATE(encodeURIComponent(serverId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async validateMasterServer(serverId: string, payload?: { profileId?: string }): Promise<ValidateMcpServerResult> {
    const result = await apiClient.post<ValidateMcpServerResult>(
      API_ENDPOINTS.MCP.SERVERS.VALIDATE(encodeURIComponent(serverId)),
      payload || {},
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async discoverMasterServerTools(
    serverId: string,
    payload?: { profileId?: string; version?: string; tools?: any[]; capabilities?: Record<string, any>; metadata?: Record<string, any> }
  ): Promise<DiscoverMcpToolsResult> {
    const result = await apiClient.post<DiscoverMcpToolsResult>(
      API_ENDPOINTS.MCP.SERVERS.DISCOVER(encodeURIComponent(serverId)),
      payload || {},
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async listMasterServerTools(serverId: string, params?: ListParams): Promise<McpToolListResult> {
    const query = buildListQuery(params);
    const path = API_ENDPOINTS.MCP.SERVERS.TOOLS(encodeURIComponent(serverId));
    const url = query ? `${path}?${query}` : path;
    const result = await apiClient.get<McpToolListResult>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async listManagedIntegrations(agentInstanceId: string): Promise<{ items: ManagedMcpIntegration[] }> {
    const result = await apiClient.get<{ items: ManagedMcpIntegration[] }>(
      API_ENDPOINTS.AGENT_INSTANCES.MCP_INTEGRATIONS.LIST(encodeURIComponent(agentInstanceId)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async listManagedIntegrationTools(agentInstanceId: string, serverId: string): Promise<{ items: McpToolDefinition[] }> {
    const result = await apiClient.get<{ items: McpToolDefinition[] }>(
      API_ENDPOINTS.AGENT_INSTANCES.MCP_INTEGRATIONS.TOOLS(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(serverId)
      ),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async connectManagedIntegration(agentInstanceId: string, serverId: string, payload: Record<string, any>) {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.AGENT_INSTANCES.MCP_INTEGRATIONS.CONNECT(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(serverId)
      ),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async startManagedOAuth(agentInstanceId: string, serverId: string, payload: StartManagedOAuthPayload) {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.AGENT_INSTANCES.MCP_INTEGRATIONS.OAUTH_START(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(serverId)
      ),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async completeManagedOAuth(payload: { state: string; code?: string; error?: string }) {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.MCP.OAUTH_COMPLETE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async completeAdminConsent(payload: { agentInstanceId: string; msTenantId: string }) {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.MCP.ADMIN_CONSENT_COMPLETE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async disconnectManagedIntegration(agentInstanceId: string, serverId: string) {
    const result = await apiClient.post<any>(
      API_ENDPOINTS.AGENT_INSTANCES.MCP_INTEGRATIONS.DISCONNECT(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(serverId)
      ),
      {},
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
};
